import React, { useEffect, useState, useCallback, useRef } from "react";

const MANIFEST_URL = "/pics/gallery/manifest.json";
const GALLERY_BASE = "/pics/gallery/";
const CASCADE_COUNT = 5;

// Small 640px-wide variant generated alongside each image (see sync-gallery.js).
// Used everywhere the image is displayed small (cascade, grid); the full-res
// original is loaded only in the lightbox single view.
const thumbUrl = (img) => GALLERY_BASE + img.replace(/\.webp$/, "_thumb.webp");

// Fisher-Yates shuffle, returns a new array
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ─── Lightbox Gallery (full-screen overlay) ───────────────────────
const Lightbox = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const [page, setPage] = useState(0);
  const [view, setView] = useState("grid"); // "grid" | "single"
  const PER_PAGE = 20;
  const totalPages = Math.ceil(images.length / PER_PAGE);
  const gridRef = useRef(null);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (view === "single") {
        if (e.key === "ArrowRight") setIdx((i) => Math.min(i + 1, images.length - 1));
        if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [view, images.length, onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const pageImages = images.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  if (view === "single") {
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose}>
        {/* Back to grid */}
        <button
          onClick={(e) => { e.stopPropagation(); setView("grid"); }}
          className="absolute top-4 left-4 text-white/70 hover:text-white text-sm flex items-center gap-2 z-10 bg-black/50 px-3 py-2 rounded-lg"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
          Gallery
        </button>

        {/* Close */}
        <button onClick={onClose} className="absolute top-4 right-4 text-white/70 hover:text-white z-10 bg-black/50 w-10 h-10 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image */}
        <img
          src={GALLERY_BASE + images[idx]}
          alt={`Screenshot ${idx + 1}`}
          className="max-h-[90vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
        />

        {/* Prev / Next */}
        {idx > 0 && (
          <button
            onClick={(e) => { e.stopPropagation(); setIdx(idx - 1); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/40 hover:bg-black/60 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        )}
        {idx < images.length - 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); setIdx(idx + 1); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/40 hover:bg-black/60 w-12 h-12 rounded-full flex items-center justify-center transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>
        )}

        {/* Counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm bg-black/50 px-3 py-1 rounded-full">
          {idx + 1} / {images.length}
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="fixed inset-0 z-[100] bg-black/95 overflow-y-auto" ref={gridRef}>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-sm border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <h2 className="text-white font-heading text-xl uppercase tracking-wider">
          Screenshot Gallery
          <span className="text-white/40 text-sm ml-3 font-normal normal-case">{images.length} images</span>
        </h2>
        <button onClick={onClose} className="text-white/70 hover:text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Grid */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {pageImages.map((img, i) => {
            const globalIdx = page * PER_PAGE + i;
            return (
              <div
                key={img}
                className="relative overflow-hidden rounded-lg cursor-pointer group aspect-video bg-gray-900"
                onClick={() => { setIdx(globalIdx); setView("single"); }}
              >
                <img
                  src={thumbUrl(img)}
                  alt={`Screenshot ${globalIdx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8 pb-4">
            <button
              onClick={() => { setPage((p) => Math.max(0, p - 1)); gridRef.current?.scrollTo(0, 0); }}
              disabled={page === 0}
              className="px-3 py-2 text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => { setPage(i); gridRef.current?.scrollTo(0, 0); }}
                className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                  i === page
                    ? "bg-hvy-accent text-hvy-900"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => { setPage((p) => Math.min(totalPages - 1, p + 1)); gridRef.current?.scrollTo(0, 0); }}
              disabled={page === totalPages - 1}
              className="px-3 py-2 text-white/60 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};


// ─── Cascade (5 random images in the 2+3 grid) ───────────────────
const Gallery = () => {
  const [allImages, setAllImages] = useState([]);
  const [cascade, setCascade] = useState([]);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Fetch manifest
  useEffect(() => {
    // Skip during static pre-render: the cascade is random/async, so populating
    // it here would not match the client's first paint and would break hydration.
    // The gallery is client-only eye-candy (not SEO content) and loads after mount.
    if (typeof window !== "undefined" && window.__PRERENDER__) return;
    fetch(MANIFEST_URL)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        setAllImages(data.images);
        setCascade(shuffle(data.images).slice(0, CASCADE_COUNT));
      })
      .catch(() => { /* silent fail – section won't render */ });
  }, []);

  const refreshCascade = useCallback(() => {
    if (allImages.length >= CASCADE_COUNT) {
      setCascade(shuffle(allImages).slice(0, CASCADE_COUNT));
    }
  }, [allImages]);

  if (cascade.length < CASCADE_COUNT) return null;

  return (
    <>
      {/* 2+3 asymmetric cascade grid */}
      <div
        className="w-full lg:w-1/3 grid grid-cols-2 gap-4 items-center cursor-pointer group/cascade"
        onClick={() => setLightboxOpen(true)}
        title="Click to open gallery"
      >
        {/* Left column: 2 images */}
        <div className="flex flex-col space-y-4">
          {cascade.slice(0, 2).map((img, i) => (
            <div key={img} className="overflow-hidden rounded-lg shadow-md">
              <img
                src={thumbUrl(img)}
                alt={`Community screenshot ${i + 1}`}
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                loading={i === 0 ? undefined : "lazy"}
              />
            </div>
          ))}
        </div>
        {/* Right column: 3 images */}
        <div className="flex flex-col space-y-4">
          {cascade.slice(2, 5).map((img, i) => (
            <div key={img} className="overflow-hidden rounded-lg shadow-md">
              <img
                src={thumbUrl(img)}
                alt={`Community screenshot ${i + 3}`}
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        {/* Hover overlay with gallery hint */}
        <div className="col-span-2 flex items-center justify-center gap-2 mt-2 text-sm text-gray-600 group-hover/cascade:text-hvy-accent transition-colors duration-300">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
          </svg>
          View all {allImages.length} screenshots
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          startIndex={0}
          onClose={() => { setLightboxOpen(false); refreshCascade(); }}
        />
      )}
    </>
  );
};

export default Gallery;
